import React from 'react';
import {useTranslation} from 'react-i18next';

import {FaIcon} from '../../FaIcon';
import {contentGapLabelKey, contentGapWhyKey, documentGaps} from './docConstants';

/**
 * Pastilla «Lectura parcial» + tooltip para un documento al que le faltó contenido.
 *
 * Existe porque la pérdida silenciosa es peor que la pérdida (card y81z5KYF): una tabla de objetivos
 * pegada como imagen en un PDF no entra al texto, el documento queda igual en «Listo», y quien lo
 * lee concluye que el dato nunca se cargó. El agente ya se entera por el MCP; esto es la mitad que
 * ve la persona.
 *
 * El tooltip contesta las tres preguntas en orden, porque nombrar el problema sin explicarlo deja al
 * usuario igual de perdido: QUÉ quedó afuera, POR QUÉ pasa (que casi siempre es tranquilizador — la
 * IA sí lo mira al analizar) y QUÉ hacer si el dato que busca no aparece.
 */
export function ContentGapsBadge({document}) {
    const {t} = useTranslation();
    const gaps = documentGaps(document);
    if (gaps.length === 0) {
        return null;
    }

    return (
        <span className="cmem-doc-gap info-icon-container">
            <FaIcon iconName="triangle-exclamation" className="cmem-doc-gap-icon"/>
            <span>{t('client_memory.content_gap.badge')}</span>
            <div className="warning-tooltip -below -rich">
                <div className="warning-tooltip-body">
                    <ContentGapsExplanation gaps={gaps}/>
                </div>
            </div>
        </span>
    );
}

/**
 * El cuerpo del aviso, sin la pastilla. Se usa tal cual en el tooltip de la fila y en el banner que
 * va arriba del crudo — un solo texto para las dos superficies, que si no divergen.
 */
export function ContentGapsExplanation({gaps}) {
    const {t} = useTranslation();
    return (
        <>
            <p className="warning-tooltip-text cmem-doc-gap-intro">
                {t('client_memory.content_gap.intro')}
            </p>
            <ul className="cmem-doc-gap-list">
                {gaps.map((gap, i) => (
                    <li key={`${gap.reason_code}-${i}`}>
                        {/* El `detail` lo compone el backend a partir del material (un nombre de
                            archivo, un número de página): va como children de React y NUNCA
                            interpolado en la key, porque puede venir inducido por un tercero. */}
                        <strong>{t(contentGapLabelKey(gap.reason_code), {count: gap.count || 1})}</strong>
                        {gap.detail ? <span className="cmem-doc-gap-detail"> ({gap.detail})</span> : null}
                        <span className="cmem-doc-gap-why"> — {t(contentGapWhyKey(gap.reason_code))}</span>
                    </li>
                ))}
            </ul>
            <p className="warning-tooltip-text cmem-doc-gap-hint">
                {t('client_memory.content_gap.hint')}
            </p>
        </>
    );
}
